import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertTriangle, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AdminTenantAssignmentCustomer, apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { useTenantFilter } from '../../contexts/TenantFilterContext';
import { DIRECT_CUSTOMERS_SELECTION } from '../../contexts/tenantSelection';
import { TenantPicker } from './tenant-assignments/TenantPicker';
import {
  isAllOnPageSelected,
  isSomeOnPageSelected,
  togglePageSelection,
  toggleSelection,
} from './tenant-assignments/selection';
import {
  AssignmentSourceFilter,
  currentTenantLabel,
  sourceFilterLabel,
  sourceFilterToTenantIdParam,
  UNASSIGNED_TENANT_FILTER,
  UNASSIGNED_TENANT_LABEL,
} from './tenant-assignments/sourceFilter';
import { reassignImpacts } from './tenant-assignments/reassignImpact';

const PAGE_SIZE = 20;

export function TenantAssignmentsPage() {
  // Globale Header-Auswahl liefert nur den Default — lokal frei änderbar, ohne
  // die globale Auswahl selbst zu beeinflussen. Nicht-Super-Admins sehen diese
  // Seite ohnehin nie (RequireSuperAdmin), daher der defensive Fallback.
  const tenantFilter = useTenantFilter();
  const initialTenant = tenantFilter.enabled ? tenantFilter.selectedTenant : DIRECT_CUSTOMERS_SELECTION;

  // Quell-Filter (nie "Alle Tenants" — immer genau eine Quelle) + Suche (backendseitig, debounced).
  const [sourceFilter, setSourceFilter] = useState<AssignmentSourceFilter>({ kind: 'tenant', tenant: initialTenant });
  const [search, setSearch] = useState('');

  // Liste (backendseitige Suche + Pagination).
  const [customers, setCustomers] = useState<AdminTenantAssignmentCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  // Checkbox-Auswahl — bleibt beim Blättern erhalten, wird bei Filter-/Suchwechsel geleert.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Tenant zuweisen (Dialog).
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTargetId, setReassignTargetId] = useState<string | null>(null);
  const [reassignTargetName, setReassignTargetName] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState(false);
  // Bestätigung der Auswirkungen — gilt nur für den gerade gewählten Ziel-Tenant.
  const [impactsConfirmed, setImpactsConfirmed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      apiService
        .getTenantCustomerAssignments({
          search: search.trim() || undefined,
          tenantId: sourceFilterToTenantIdParam(sourceFilter),
          page,
          limit: PAGE_SIZE,
        })
        .then((result) => {
          setCustomers(result.data);
          setTotalPages(result.pagination.totalPages || 1);
        })
        .catch((err: unknown) => { toast.error(extractErrorMessage(err, 'Kunden konnten nicht geladen werden.')); })
        .finally(() => { setLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); };
  }, [search, sourceFilter, page, reloadKey]);

  const handleSearchChange = (value: string): void => {
    setSearch(value);
    setPage(1);
    setSelected(new Set());
  };

  const handleSourceTenantSelect = (tenant: { id: string; name: string }): void => {
    setSourceFilter({ kind: 'tenant', tenant: { id: tenant.id, name: tenant.name } });
    setPage(1);
    setSelected(new Set());
  };

  const handleSelectUnassignedSource = (): void => {
    setSourceFilter({ kind: 'unassigned' });
    setPage(1);
    setSelected(new Set());
  };

  const pageIds = customers.map((customer) => customer.id);
  const allOnPageSelected = isAllOnPageSelected(selected, pageIds);
  const someOnPageSelected = isSomeOnPageSelected(selected, pageIds);

  const toggleRow = (id: string, checked: boolean): void => {
    setSelected((prev) => toggleSelection(prev, id, checked));
  };

  const togglePage = (checked: boolean): void => {
    setSelected((prev) => togglePageSelection(prev, pageIds, checked));
  };

  const openReassignDialog = (): void => {
    setReassignTargetId(null);
    setReassignTargetName(null);
    setImpactsConfirmed(false);
    setReassignOpen(true);
  };

  const handleReassign = async (): Promise<void> => {
    if (!reassignTargetId) {
      toast.error('Bitte einen Ziel-Tenant auswählen.');
      return;
    }
    if (!impactsConfirmed) {
      toast.error('Bitte die Auswirkungen bestätigen.');
      return;
    }
    setReassigning(true);
    try {
      const result = await apiService.reassignCustomersToTenant({
        customerIds: [...selected],
        targetTenantId: reassignTargetId,
      });
      toast.success(`${result.movedCount} Kunden verschoben.`);
      setReassignOpen(false);
      setSelected(new Set());
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Kunden konnten nicht verschoben werden.'));
    } finally {
      setReassigning(false);
    }
  };

  const sourceTenantId = sourceFilter.kind === 'unassigned' ? UNASSIGNED_TENANT_FILTER : sourceFilter.tenant.id;
  const excludeTenantId = sourceFilter.kind === 'tenant' ? sourceFilter.tenant.id : null;
  const impacts = reassignImpacts(sourceFilter, reassignTargetId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <ArrowRightLeft className="w-6 h-6 text-teal-600" />
          Kunden-Zuordnung
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Kunden einem anderen Tenant zuweisen. Verschieben ist eine Ausnahme im Normalbetrieb — nur Super-Admins
          haben Zugriff.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Kunden</CardTitle>
          <CardDescription>Nach Tenant filtern und/oder nach Name, E-Mail oder ID suchen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label>Quell-Tenant</Label>
              <TenantPicker
                selectedTenantId={sourceTenantId}
                selectedTenantName={sourceFilterLabel(sourceFilter)}
                onSelect={handleSourceTenantSelect}
                extraOption={{
                  id: UNASSIGNED_TENANT_FILTER,
                  label: UNASSIGNED_TENANT_LABEL,
                  onSelect: handleSelectUnassignedSource,
                }}
                placeholder="Tenant filtern…"
                triggerAriaLabel="Quell-Tenant filtern"
              />
            </div>
            <div className="space-y-1 flex-1 min-w-[220px]">
              <Label htmlFor="assignment-search">Suche</Label>
              <Input
                id="assignment-search"
                placeholder="Name, E-Mail oder ID…"
                value={search}
                onChange={(e) => { handleSearchChange(e.target.value); }}
              />
            </div>
          </div>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-gray-50 px-3 py-2">
              <span className="text-sm font-medium text-gray-700">{selected.size} Kunden ausgewählt</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={openReassignDialog}>
                  Tenant zuweisen…
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setSelected(new Set()); }}>
                  Auswahl aufheben
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Lade Kunden…</div>
          ) : customers.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              {search.trim() ? 'Keine Kunden zu den Filtern gefunden.' : 'Keine Kunden vorhanden.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false}
                      onCheckedChange={(checked) => { togglePage(checked === true); }}
                      aria-label="Alle Kunden dieser Seite auswählen"
                    />
                  </TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktueller Tenant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(customer.id)}
                        onCheckedChange={(checked) => { toggleRow(customer.id, checked === true); }}
                        aria-label={`${customer.name} auswählen`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{customer.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{customer.id}</div>
                    </TableCell>
                    <TableCell className="text-gray-600">{customer.email ?? '—'}</TableCell>
                    <TableCell>
                      {customer.isActive ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">Inaktiv</Badge>
                      )}
                    </TableCell>
                    <TableCell
                      className={customer.tenantName === null ? 'italic text-gray-400' : 'text-gray-600'}
                    >
                      {currentTenantLabel(customer.tenantName)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => { setPage((p) => p - 1); }}
            >
              Zurück
            </Button>
            <span className="text-xs text-gray-500">
              Seite {page} von {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => { setPage((p) => p + 1); }}
            >
              Weiter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tenant zuweisen */}
      <Dialog
        open={reassignOpen}
        onOpenChange={(open) => {
          setReassignOpen(open);
          if (!open) {
            setReassignTargetId(null);
            setReassignTargetName(null);
            setImpactsConfirmed(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tenant zuweisen</DialogTitle>
            <DialogDescription>
              {selected.size} Kunden werden dem gewählten Ziel-Tenant zugeordnet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Ziel-Tenant</Label>
              <TenantPicker
                selectedTenantId={reassignTargetId}
                selectedTenantName={reassignTargetName}
                onSelect={(tenant) => {
                  setReassignTargetId(tenant.id);
                  setReassignTargetName(tenant.name);
                  setImpactsConfirmed(false);
                }}
                excludeTenantId={excludeTenantId}
                placeholder="Ziel-Tenant suchen…"
                triggerAriaLabel="Ziel-Tenant auswählen"
                className="w-full"
              />
            </div>
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="space-y-2">
                <p className="font-medium">
                  Verschieben ist eine Ausnahme. Mach dir vorher klar, welche Auswirkungen das für die Kunden hat:
                </p>
                <ul className="list-disc space-y-1 pl-4">
                  {impacts.map((impact) => (
                    <li key={impact}>{impact}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="reassign-confirm"
                checked={impactsConfirmed}
                disabled={!reassignTargetId || reassigning}
                onCheckedChange={(checked) => { setImpactsConfirmed(checked === true); }}
              />
              <Label htmlFor="reassign-confirm" className="text-sm font-normal leading-snug">
                Ich habe die Auswirkungen gelesen und möchte {selected.size} Kunden
                {reassignTargetName ? ` nach „${reassignTargetName}“` : ''} verschieben.
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setReassignOpen(false); }} disabled={reassigning}>
              Abbrechen
            </Button>
            <Button type="button" onClick={() => { void handleReassign(); }} disabled={reassigning || !reassignTargetId || !impactsConfirmed}>
              {reassigning ? 'Wird verschoben…' : 'Verschieben'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
